import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { useFormContext, useWatch } from "react-hook-form";

import { GeometryDataColumns } from "./columnNames/NozzleColumns";
import { compactTableProps } from "../../../../../../styles/muiProps";

import {
  useResponsivePvTable,
  getResponsivePvTableProps,
  PV_TABLE_ROW_HEIGHT,
} from "./responsivePvTable";

const GEOMETRY_COLUMN_WEIGHTS = {
  "Dist From DL": 1,
  Orientation: 1.05,
  "Dist From CL (+/-)": 1.1,
  Direction: 1.05,
  Location: 1.05,
  "Nozzle Material": 1.2,
  "Flange Material": 1.2,
  "Pad Material": 1.15,
};

const GeometryDataTable = (props) => {
  const { rows, handleSaveCell } = props;

  // Read-only material values come from the Design tab material section
  // at the bottom (Nozzle Pipe / Flange-Forging / RF Pad). Support both
  // the clean AMS keys and the legacy ComboBox12/13/14 keys.
  const { control } = useFormContext();
  const nozzleMaterialClean = useWatch({ control, name: "nozzleMaterial" });
  const nozzleMaterialLegacy = useWatch({ control, name: "ComboBox12" });
  const flangeMaterialClean = useWatch({ control, name: "flangeMaterial" });
  const flangeMaterialLegacy = useWatch({ control, name: "ComboBox13" });
  const padMaterialClean = useWatch({ control, name: "rfPadMaterial" });
  const padMaterialLegacy = useWatch({ control, name: "ComboBox14" });

  const designNozzleMaterial = nozzleMaterialClean ?? nozzleMaterialLegacy ?? "";
  const designFlangeMaterial = flangeMaterialClean ?? flangeMaterialLegacy ?? "";
  const designPadMaterial = padMaterialClean ?? padMaterialLegacy ?? "";

  // Overlay the Design materials so the last 3 columns always show the
  // Design tab values, even if a stored row still holds an old value.
  const displayRows = useMemo(
    () =>
      (rows ?? []).map((row) => ({
        ...row,
        "Nozzle Material": designNozzleMaterial,
        "Flange Material": designFlangeMaterial,
        "Pad Material": designPadMaterial,
      })),
    [rows, designNozzleMaterial, designFlangeMaterial, designPadMaterial],
  );

  const {
    viewportRef,
    tableWidth,
    headerFontSize,
    bodyFontSize,
    columns: responsiveColumns,
  } = useResponsivePvTable(GeometryDataColumns ?? [], GEOMETRY_COLUMN_WEIGHTS);

  const responsiveTableProps = getResponsivePvTableProps({
    tableWidth,
    headerFontSize,
    bodyFontSize,
  });

  return (
    <Box
      ref={viewportRef}
      sx={{
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        overflowX: "hidden",
      }}
    >
      <MaterialReactTable
        {...compactTableProps}
        {...responsiveTableProps}
        columns={responsiveColumns ?? []}
        data={displayRows ?? []}
        enableTopToolbar={false}
        enableColumnActions={false}
        editDisplayMode="table"
        enableEditing
        enableSorting={false}
        muiEditTextFieldProps={({ cell }) => ({
          onBlur: (event) => {
            handleSaveCell(cell, event.target.value, "DataGridView10");
          },

          fullWidth: true,

          sx: {
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",

            height: `${PV_TABLE_ROW_HEIGHT}px`,

            "& .MuiInputBase-root": {
              width: "100%",
              minWidth: "0 !important",
              maxWidth: "100%",

              height: `${PV_TABLE_ROW_HEIGHT}px !important`,
              minHeight: `${PV_TABLE_ROW_HEIGHT}px !important`,

              overflow: "hidden",
            },

            "& .MuiInputBase-input": {
              width: "100%",
              minWidth: "0 !important",

              height: `${PV_TABLE_ROW_HEIGHT}px`,

              boxSizing: "border-box",

              padding: "0 2px",

              textAlign: "center",

              fontSize: `${bodyFontSize}px !important`,
            },

            "& .MuiSelect-select": {
              height: `${PV_TABLE_ROW_HEIGHT}px !important`,

              boxSizing: "border-box",

              display: "flex",
              alignItems: "center",

              py: "0 !important",

              textAlign: "center",
            },
          },
        })}
      />
    </Box>
  );
};

export default GeometryDataTable;
